import * as currentDate from "currentDate";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as spacedRepetition from "shared/spacedRepetition";
import { TypedEventEmitter } from "shared/TypedEventEmitter";
import UserStore from "stores/UserStore";
import UserWordStore, { UserWordData } from "stores/UserWordStore";

type EventName = "groupsUpdated" | "groupsReCreated";

type PracticeEvents = {
  groupsUpdated: [];
  groupsReCreated: [];
};

type UserWordsResponse = {
  userWords: UserWordData[];
  count: number;
};

export default class PracticeStore {
  private user: UserStore;
  private wordsById: Map<string, UserWordStore> = new Map();
  private readyToTestWords: UserWordStore[] = [];
  private notStartedWords: UserWordStore[] = [];
  // Total number of words ready to test, taken from the inline count of the
  // readyToTest fetch. null until the readyToTest group has been fetched.
  private readyToTestCount: number | null = null;
  private events = new TypedEventEmitter<PracticeEvents>();
  private groupsUpdatedTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(user: UserStore) {
    this.user = user;

    this.user.onChange(
      (attrs) => attrs.currentLearningLanguage,
      () => {
        if (this.readyToTestCount !== null) {
          this.resetGroups();
          this.emitGroupsUpdated();
          this.emit("groupsReCreated");
        }
      }
    );
  }

  on(event: EventName, callback: () => void) {
    return this.events.on(event, callback);
  }

  off(event: EventName, callback: () => void) {
    this.events.off(event, callback);
  }

  private emit(event: EventName) {
    this.events.trigger(event);
  }

  emitGroupsUpdated() {
    if (this.groupsUpdatedTimer !== null) {
      clearTimeout(this.groupsUpdatedTimer);
    }
    this.groupsUpdatedTimer = setTimeout(() => {
      this.emit("groupsUpdated");
    }, 0);
  }

  resetGroups() {
    this.wordsById = new Map();
    this.readyToTestWords = [];
    this.notStartedWords = [];
    this.readyToTestCount = null;
  }

  get(id: string): UserWordStore | undefined {
    return this.wordsById.get(id);
  }

  /** Returns all words as an array, for use as distractors in multiple choice challenges */
  filter(predicate: (word: UserWordStore) => boolean): UserWordStore[] {
    return Array.from(this.wordsById.values()).filter(predicate);
  }

  add(word: UserWordStore) {
    this.wordsById.set(String(word.id), word);
  }

  remove(word: UserWordStore) {
    this.wordsById.delete(String(word.id));
    this.readyToTestWords = this.readyToTestWords.filter(
      (w) => w.id !== word.id
    );
    this.notStartedWords = this.notStartedWords.filter((w) => w.id !== word.id);
  }

  /**
   * Total number of words ready to test, or null if the readyToTest group has
   * not been fetched yet. Sourced from the inline count of the readyToTest
   * userWords response, so it is the full total, not capped by the fetch limit.
   */
  getReadyToTestCount(): number | null {
    return this.readyToTestCount;
  }

  private addWordsToIndex(words: UserWordStore[]) {
    for (const word of words) {
      this.wordsById.set(String(word.id), word);
    }
  }

  private getOrCreateWord(wordData: UserWordData): UserWordStore {
    const existing = this.wordsById.get(wordData._id);
    if (existing) {
      existing.patch(wordData);
      return existing;
    }
    return new UserWordStore(wordData);
  }

  private async fetchWords(
    data: Record<string, any>
  ): Promise<{ words: UserWordStore[]; count: number }> {
    const response = await readlangClient.request<UserWordsResponse>(
      "GET",
      "/api/v2/userWords",
      data
    );
    const words = response.data.userWords.map((wordData) =>
      this.getOrCreateWord(wordData)
    );
    this.addWordsToIndex(words);
    return { words, count: response.data.count };
  }

  async fetchGroup(group: string): Promise<UserWordStore[]> {
    const data: Record<string, any> = {
      language: this.user.currentLearningLanguage.value,
    };
    if (group !== "all") {
      data.wordGroup = group;
    }

    if (group === "readyToTest") {
      data.limit = 60;
    }

    if (group === "reviewedToday") {
      data.wordGroup = "reviewedSince";
      data.limit = 9999;

      const date = await currentDate.getSnapped(0);
      data.timestamp = Math.round(date.getTime() / 1000);
    }

    const { words, count } = await this.fetchWords(data);

    if (group === "readyToTest") {
      this.readyToTestWords = words;
      this.readyToTestCount = count;
    }
    if (group === "notStarted") {
      this.notStartedWords = words;
    }

    return words;
  }

  async fetchGroups(
    groups: string[]
  ): Promise<Record<string, UserWordStore[]>> {
    const fetched = await Promise.all(
      groups.map((group) => this.fetchGroup(group))
    );

    const result: Record<string, UserWordStore[]> = {};
    for (const [index, group] of groups.entries()) {
      result[group] = fetched[index];
    }
    return result;
  }

  async fetchBookWordsToTest(bookIDs: string[]): Promise<UserWordStore[]> {
    const data = {
      bookIDs: bookIDs.join("|"),
      wordGroup: "readyToTest",
      language: this.user.currentLearningLanguage.value,
      limit: 60,
    };

    let { words } = await this.fetchWords(data);

    if (words.length < 5) {
      words = (
        await this.fetchWords({
          ...data,
          wordGroup: undefined,
        })
      ).words;
    }

    return words;
  }

  async fetchSourceWordsToTest(source: string): Promise<UserWordStore[]> {
    const data = {
      source,
      wordGroup: "readyToTest",
      language: this.user.currentLearningLanguage.value,
      limit: 60,
    };

    let { words } = await this.fetchWords(data);

    if (words.length < 5) {
      words = (
        await this.fetchWords({
          ...data,
          wordGroup: undefined,
        })
      ).words;
    }

    return words;
  }

  async reportRecallEase(
    currentWord: UserWordStore,
    ease: number,
    difficulty: number,
    source: spacedRepetition.RecallSource,
    repeatUntilPerfect: boolean = false
  ): Promise<number> {
    const date = await currentDate.get();

    spacedRepetition.wordRecalled(currentWord, ease, date, difficulty, {
      ...spacedRepetition.reviewPaceSettingsFor(
        this.user.state.value.reviewPace
      ),
      timezoneOffset: dateUtils.getTimezoneOffset(),
      source,
    });

    const toWait = (() => {
      if (ease < 2) {
        return 5;
      } else if (ease < 3) {
        return 8;
      } else if (
        currentWord.state.value.passiveOverride ||
        repeatUntilPerfect
      ) {
        return 20;
      }
      return -1;
    })();

    if (toWait === -1) {
      this.readyToTestWords = this.readyToTestWords.filter(
        (word) => word.id !== currentWord.id
      );
    } else {
      this.readyToTestWords = this.readyToTestWords.filter(
        (word) => word.id !== currentWord.id
      );
      const insertIndex = Math.max(0, this.readyToTestWords.length - toWait);
      this.readyToTestWords = [
        ...this.readyToTestWords.slice(0, insertIndex),
        currentWord,
        ...this.readyToTestWords.slice(insertIndex),
      ];
    }

    return toWait;
  }

  async completedToday(): Promise<UserWordStore[]> {
    const result: UserWordStore[] = [];

    const date = await currentDate.getSnapped(this.user.state.value.updateHour);
    const lowerBound = date.getTime();
    const upperBound = lowerBound + spacedRepetition.DAY;

    for (const [, userWord] of this.wordsById) {
      const nextDate = userWord.state.value.nextDate;
      const interval = userWord.state.value.interval;

      if (nextDate && interval) {
        const previousDate =
          new Date(dateUtils.parse(nextDate)).getTime() - Number(interval);
        if (previousDate > lowerBound && previousDate <= upperBound) {
          result.push(userWord);
        }
      }
    }

    return result;
  }
}
